import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { CjsCustomList, CustomListOptions, CustomListPropertyConfigTypes, defaultCustomListPropertyConfigType, PropertyConfigValueType } from "../../components/customList/customList";
import { SHOULD_FINISH_ON_ENTER_FUNCTION } from "../../components/smartTable/smartTableEdition.helper";
import { CjsEditor, defaultEditorOptions, EditorOptions } from "../editor";

ensureLoaded(CjsCustomList);

export type CustomListEditorOptions = EditorOptions;

@CustomElement({
    name: "cjs-custom-list-editor",
    elementCssClasses: [FLEX_CONTAINER_COLUMN, "position-relative"]
})
export class CjsCustomListEditor<P extends CustomListPropertyConfigTypes = typeof defaultCustomListPropertyConfigType> extends CjsEditor<PropertyConfigValueType<P>[]> {

    /** CustomList component used in the editor */
    public customList: CjsCustomList<P>;

    @TypedWebComponentOptions(defaultEditorOptions)
    public readonly options!: WebComponentOptions<CustomListEditorOptions>;

    /** Direct access to customList options */
    public get customListOptions(): WebComponentOptions<CustomListOptions<P>> {
        return this.customList.options;
    }

    constructor(options?: Partial<CustomListEditorOptions>, customListOptions?: Partial<CustomListOptions<P>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        this.options.setWithoutRefresh(options);

        this.customList = new CjsCustomList(customListOptions);
    }

    /** @inheritdoc */
    public getInput(): CjsCustomList<P> {
        return this.customList;
    }

    /** @inheritdoc */
    public override init(): void {
        super.init();

        // Should add customList dynamically
        this.customList.classList.add(FLEX_CONTAINER, FLEX_ITEM_AUTO);

        // Trigger _onInputValueChange on customList event emitted
        this.customList.onAddEvent.on(() => this._onInputValueChange());
        this.customList.onDeleteEvent.on(() => this._onInputValueChange());

        // Add customList to customListEditor
        this.append(this.customList);
    }

    /** @inheritdoc */
    public override tryEdit(): void {
        // Open the list
        this.customList.openPopup();
    }

    /**
     * @returns validationFormElement
     * Where "is-invalid" class will be added / removed
     */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().components.container;
    }

    /** @inheritdoc */
    protected override _refreshInputValue(): void {
        this.customList.setData(this.getValue() ?? []);
    }

    /** @inheritdoc */
    protected _getValueFromInputComponent(): PropertyConfigValueType<P>[] | undefined {
        return this.getInput().itemsList;
    }

    /** @inheritdoc */
    protected override _refreshDisabled(): void {
        this.customList.options.setAndRefreshIfChanged({ disabled: this.disabled });
    }
    /**
     * This function is used by SmartTableEditionHelper in order to prevent the smart table from finishing edition on enter
     */
    public shouldSmartTableFinishValueEditionOnEnter(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const refToCode = SHOULD_FINISH_ON_ENTER_FUNCTION;// this variable is here only to link to the code that uses this function
        return false;
    }
}